import React from 'react';

function TrustedBy() {
  const features = [
    {
      icon: (
        <svg className="w-10 h-10 mb-2 md:w-12 md:h-12" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M2 5a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 01-2 2H4a2 2 0 01-2-2V5zm14 1a1 1 0 11-2 0 1 1 0 012 0zM2 13a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 01-2 2H4a2 2 0 01-2-2v-2zm14 1a1 1 0 11-2 0 1 1 0 012 0z" clipRule="evenodd" />
        </svg>
      ),
      title: "99.99% Uptime",
      description: "Zero maintenance downtime for continuous service"
    },
    {
      icon: (
        <svg className="w-10 h-10 mb-2 md:w-12 md:h-12" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
        </svg>
      ),
      title: "Security Monitoring",
      description: "Zero Trust security architecture trusted by enterprises"
    },
    {
      icon: (
        <svg className="w-10 h-10 mb-2 md:w-12 md:h-12" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z" clipRule="evenodd" />
        </svg>
      ),
      title: "In-House Tooling",
      description: "Creating functional dashboards to visualize your data"
    },
    {
      icon: (
        <svg className="w-10 h-10 mb-2 md:w-12 md:h-12" fill="currentColor" viewBox="0 0 20 20">
          <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
        </svg>
      ),
      title: "24/7 Response Team",
      description: "Always monitoring and looking after your infrastructure"
    }
  ];

  return (
    <section id="trusted-by" className="bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900">
      <div className="items-center max-w-screen-xl px-4 py-16 mx-auto lg:grid lg:grid-cols-4 lg:gap-16 xl:gap-24 lg:py-24 lg:px-6">
        <div className="col-span-2 mb-8 animate-fade-in">
          <p className="text-lg font-semibold text-accent-600 dark:text-accent-400 mb-3">
            Trusted Worldwide
          </p>
          <h2 className="mt-3 mb-4 text-3xl font-extrabold tracking-tight text-gray-900 md:text-4xl dark:text-white">
            Trusted by Leading Companies
          </h2>
          <p className="text-gray-600 dark:text-gray-300 text-lg leading-relaxed">
            Our rigorous security and compliance standards are at the heart of everything we do. We work tirelessly to protect you and your customers with enterprise-grade solutions.
          </p>

          <div className="pt-6 mt-6 space-y-4 border-t border-gray-200 dark:border-gray-700">
            <div>
              <a
                href="#contact"
                className="inline-flex items-center text-base font-semibold text-accent-600 hover:text-accent-800 dark:text-accent-400 dark:hover:text-accent-300 transition-colors duration-300 group"
                aria-label="Visit our help center for support"
              >
                Visit the Help Center
                <svg
                  className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-300"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
            </div>
          </div>
        </div>

        <div className="col-span-2 space-y-8 md:grid md:grid-cols-2 md:gap-12 md:space-y-0">
          {features.map((feature, index) => (
            <div
              key={index}
              className="p-6 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-300 group animate-fade-in"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="text-accent-600 dark:text-accent-400 mb-4 group-hover:scale-110 transition-transform duration-300">
                {feature.icon}
              </div>
              <h3 className="mb-2 text-2xl font-bold text-gray-900 dark:text-white">
                {feature.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default TrustedBy;
